/*
 * Loading state for the cart checkout buttons.
 *
 * Clicking checkout is not instant on this store. assets/gift-manager.js
 * intercepts the submit to run one last reconciliation (200-400ms typically,
 * capped at 4s), refreshes the cart sections, and only then re-fires the
 * button — and the redirect to /checkout takes its own time on top. Without
 * this the button looks inert for all of it and gets clicked again.
 *
 * Lives on its own rather than inside gift-manager.js because the wait is not
 * the gift engine's alone: the redirect happens whether or not a campaign is
 * running, and gift-manager.js only loads when one is configured.
 *
 * Loaded once from layout/theme.liquid, under the same
 * `cart_type == 'drawer' or template.name == 'cart'` gate the other cart-wide
 * scripts use, since that is exactly when a checkout surface exists. Listeners
 * are delegated from `document`, so a second copy would double-fire.
 */
(function () {
  'use strict';

  var CHECKOUT_SELECTOR = '.cart__checkout-button';

  /*
   * Dawn's button loading treatment (assets/product-form.js): `.loading` turns
   * the label transparent, the sibling spinner is un-hidden.
   *
   * aria-disabled, not `disabled`. A real disable would drop focus to the body
   * and — worse — assets/gift-manager.js re-fires checkout with
   * `if (btn && !btn.disabled) btn.click()`, so disabling the button here would
   * silently swallow the very submit this spinner is reporting on.
   */
  function markLoading(button) {
    if (!button) return;
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  var pending = false;
  var observer = null;

  /*
   * Re-apply to every checkout button in the document.
   *
   * Both surfaces are covered on purpose, not just the one that submitted: the
   * drawer and the cart page each render a checkout button, and the customer is
   * leaving the page either way, so neither should look ready for another
   * click.
   */
  function applyPending() {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(markLoading);
  }

  /*
   * The reconciliation in gift-manager.js refreshes the cart sections through
   * the Section Rendering API, which replaces the checkout button with a fresh
   * server-rendered one carrying none of the state set above. That lands in the
   * middle of the wait — after the reconcile, right before the re-fire — so
   * without re-applying, the spinner disappears for the whole redirect.
   *
   * The observer is only ever started once a checkout is under way, and the
   * page navigates away shortly after, so it does not outlive its purpose.
   */
  function watchForReplacement() {
    if (observer || typeof MutationObserver === 'undefined') return;
    observer = new MutationObserver(applyPending);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /*
   * Bubble phase, deliberately. assets/international-orders-checkbox.js blocks
   * an unticked shipping agreement from the capture phase with
   * stopPropagation(), so a refused checkout never reaches this handler and
   * never spins.
   *
   * Nothing here calls preventDefault: the submit must proceed untouched, and
   * gift-manager.js's own handler (also bubble-phase, on `document`) owns
   * whether it is deferred.
   */
  document.addEventListener('submit', function (event) {
    var submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (pending) return;
    pending = true;
    applyPending();
    watchForReplacement();
  });

  /*
   * Back/forward cache restores the page with the spinner still spinning — the
   * customer returning from checkout would find a dead-looking button. Clearing
   * on pageshow is enough: any bfcache restore fires it.
   */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    pending = false;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (button) {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      var spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    });
  });
})();
